import { useEffect, useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { useT } from "@/i18n/I18nProvider";
import { LanguageToggle } from "./LanguageToggle";
import { Menu, X, ChevronDown } from "lucide-react";
import type { RouteKey } from "@/lib/routes";

interface NavGroup {
  key: string;
  labelKey: "services" | "commodities" | "compliance" | "resources";
  parent: RouteKey;
  children: RouteKey[];
}

const NAV_GROUPS: NavGroup[] = [
  {
    key: "services",
    labelKey: "services",
    parent: "services.index",
    children: [
      "services.trade",
      "services.sourcing",
      "services.supply",
    ],
  },
  {
    key: "commodities",
    labelKey: "commodities",
    parent: "commodities.index",
    children: [
      "commodities.grains",
      "commodities.food",
      "commodities.petrochemical",
    ],
  },
  {
    key: "compliance",
    labelKey: "compliance",
    parent: "compliance.index",
    children: [
      "compliance.corporate",
      "compliance.kyc",
      "compliance.antifraud",
    ],
  },
  {
    key: "resources",
    labelKey: "resources",
    parent: "resources.index",
    children: [
      "resources.profile",
      "resources.trade-docs",
    ],
  },
];

export function Header() {
  const { t, path } = useT();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [hoveredGroup, setHoveredGroup] = useState<string | null>(null);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = mobileOpen ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [mobileOpen]);

  return (
    <header
      className={
        "sticky top-0 z-50 bg-bone/85 backdrop-blur-xl transition-[padding,box-shadow,background-color] duration-300 border-b " +
        (scrolled ? "border-bone-500" : "border-transparent")
      }
      data-testid="site-header"
    >
      <a href="#main" className="sr-only focus:not-sr-only focus:absolute focus:top-2 focus:left-4 focus:bg-ink focus:text-bone focus:px-3 focus:py-2 focus:z-50">
        {t.nav.skip}
      </a>
      <div className="container-bnm flex items-center justify-between gap-6 py-4 md:py-5">
        <Link
          to={path("home")}
          className="flex items-center gap-3 group shrink-0"
          data-testid="header-logo"
        >
          <img
            src="/images/brand/bnm-logo.png"
            alt="BNM Group"
            className="h-9 md:h-10 w-auto"
            width={727}
            height={320}
          />
          <span className="hidden 2xl:inline font-mono text-[10px] uppercase tracking-eyebrow text-slate2 whitespace-nowrap">
            LLC — {t.meta.tagline}
          </span>
          <span className="hidden sm:inline 2xl:hidden font-mono text-[10px] uppercase tracking-eyebrow text-slate2">
            LLC
          </span>
        </Link>

        {/* Desktop nav */}
        <nav aria-label="Primary" className="hidden lg:flex items-center gap-7 xl:gap-9">
          <NavLink
            to={path("about")}
            className={({ isActive }) =>
              "whitespace-nowrap font-mono text-[11px] uppercase tracking-eyebrow transition-colors " +
              (isActive ? "text-clay" : "text-ink hover:text-clay")
            }
            data-testid="nav-about"
          >
            {t.nav.about}
          </NavLink>

          {NAV_GROUPS.map((g) => (
            <div
              key={g.key}
              className="relative"
              onMouseEnter={() => setHoveredGroup(g.key)}
              onMouseLeave={() => setHoveredGroup(null)}
              onFocus={() => setHoveredGroup(g.key)}
              onBlur={(e) => {
                if (!e.currentTarget.contains(e.relatedTarget as Node)) setHoveredGroup(null);
              }}
            >
              <NavLink
                to={path(g.parent)}
                className={({ isActive }) =>
                  "flex items-center gap-1 whitespace-nowrap font-mono text-[11px] uppercase tracking-eyebrow transition-colors " +
                  (isActive ? "text-clay" : "text-ink hover:text-clay")
                }
                data-testid={`nav-${g.key}`}
              >
                {t.nav[g.labelKey]}
                <ChevronDown size={12} className="opacity-60" aria-hidden />
              </NavLink>
              {hoveredGroup === g.key && (
                <div
                  className="absolute left-1/2 -translate-x-1/2 top-full pt-4 min-w-[280px]"
                  role="menu"
                >
                  <div className="bg-bone border border-bone-500 shadow-[0_20px_50px_-20px_rgba(10,17,40,0.25)] p-2">
                    {g.children.map((childKey, idx) => (
                      <Link
                        key={childKey}
                        to={path(childKey)}
                        role="menuitem"
                        className="flex items-baseline gap-3 px-4 py-3 hover:bg-bone-300 transition-colors group"
                        data-testid={`nav-${g.key}-${childKey.split(".").pop()}`}
                      >
                        <span className="font-mono text-[10px] text-slate2 pt-0.5">
                          {String(idx + 1).padStart(2, "0")}
                        </span>
                        <span className="text-ink text-sm group-hover:text-clay transition-colors">
                          {t.routes[childKey].label}
                        </span>
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ))}

          <NavLink
            to={path("contact")}
            className={({ isActive }) =>
              "whitespace-nowrap font-mono text-[11px] uppercase tracking-eyebrow transition-colors " +
              (isActive ? "text-clay" : "text-ink hover:text-clay")
            }
            data-testid="nav-contact"
          >
            {t.nav.contact}
          </NavLink>
        </nav>

        <div className="flex items-center gap-5 md:gap-6 shrink-0 lg:pl-4 lg:border-l lg:border-bone-500 text-ink">
          <LanguageToggle testId="lang-toggle-header" />
          <button
            type="button"
            className="lg:hidden text-ink"
            aria-label={mobileOpen ? t.nav.closeMenu : t.nav.openMenu}
            aria-expanded={mobileOpen}
            onClick={() => setMobileOpen((v) => !v)}
            data-testid="mobile-menu-toggle"
          >
            {mobileOpen ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      {/* Mobile drawer */}
      {mobileOpen && (
        <div
          className="lg:hidden bg-bone border-t border-bone-500 h-[calc(100vh-64px)] overflow-y-auto"
          data-testid="mobile-menu"
        >
          <div className="container-bnm py-8 flex flex-col gap-8">
            <MobileLink to={path("about")} label={t.nav.about} onClick={() => setMobileOpen(false)} testId="mnav-about" />
            {NAV_GROUPS.map((g, gi) => (
              <div key={g.key} className="flex flex-col gap-3">
                <Link
                  to={path(g.parent)}
                  onClick={() => setMobileOpen(false)}
                  className="flex items-baseline justify-between border-b border-bone-500 pb-2"
                  data-testid={`mnav-${g.key}`}
                >
                  <span className="font-display text-xl text-ink">{t.nav[g.labelKey]}</span>
                  <span className="font-mono text-[10px] text-slate2">{String(gi + 1).padStart(2, "0")}</span>
                </Link>
                <div className="flex flex-col">
                  {g.children.map((child) => (
                    <Link
                      key={child}
                      to={path(child)}
                      onClick={() => setMobileOpen(false)}
                      className="py-2 text-slate2 hover:text-clay transition-colors text-[15px]"
                      data-testid={`mnav-${g.key}-${child.split(".").pop()}`}
                    >
                      {t.routes[child].label}
                    </Link>
                  ))}
                </div>
              </div>
            ))}
            <MobileLink to={path("contact")} label={t.nav.contact} onClick={() => setMobileOpen(false)} testId="mnav-contact" />
            <div className="pt-8 border-t border-bone-500">
              <span className="eyebrow block mb-3">{t.meta.langLabel}</span>
              <LanguageToggle testId="lang-toggle-mobile" />
            </div>
            <p className="font-mono text-[10px] uppercase tracking-eyebrow text-slate2 pt-6 flex items-center gap-3">
              <img
                src="/images/brand/bnm-logo.png"
                alt="BNM Group"
                className="h-7 w-auto"
                width={727}
                height={320}
              />
              <span>LLC · {t.meta.tagline}</span>
            </p>
          </div>
        </div>
      )}
    </header>
  );
}

function MobileLink({ to, label, onClick, testId }: { to: string; label: string; onClick: () => void; testId: string }) {
  return (
    <Link
      to={to}
      onClick={onClick}
      className="font-display text-2xl text-ink border-b border-bone-500 pb-3"
      data-testid={testId}
    >
      {label}
    </Link>
  );
}
